/**
 * FlowArt.tsx — the one component that renders a tool's generated preview art.
 *
 * There is ONE component, and it takes no styling decision from its caller: a card knows its box, its
 * tool's name and — sometimes — its tool's shape, and that is the whole of the interface. What the tile
 * looks like is a function of the flow itself, so the same tool draws the same picture in the wizard
 * plate, on a profile tile and in a search row.
 *
 * It began as five interchangeable treatments (circuit, blueprint, strata, ribbon, orbit) with a
 * per-browser override and a wizard control to cycle them. That is gone, and the reason is worth
 * keeping: five treatments meant five sets of small-size compromises to maintain and five chances for a
 * mark to overlap, and a tile whose appearance depended on the viewer's browser could not honestly be
 * described as a picture of the automation. One scene, tuned properly, beats five tuned adequately.
 *
 * THE TWO TIERS, and why the difference is deliberately visible:
 *
 *   STRUCTURAL — `shape` resolves to a step list, so the art is a DIAGRAM of the real automation:
 *     rails, boxes, traces, connections. Every mark corresponds to something in the flow.
 *
 *   EMBLEM — `shape` is absent, null or unreadable, so the art is an IDENTITY MARK: arcs, a monogram,
 *     connector pips. No nodes, no edges, nothing that could be mistaken for a step.
 *
 * The two grammars share nothing on purpose. `packages/api/src/dataverse/components.ts` sets the rule
 * that an unreadable definition must not be presented as a simple flow, and a plausible three-box
 * diagram drawn from a name and a seed would be exactly that — a lie a viewer has no way to detect.
 * Someone who has seen one tile of each can tell them apart without being told which is which.
 *
 * WHAT IS NOT HERE, and why:
 *
 *   No `getComputedStyle`. `Topography.tsx:115` reads tokens back into JS because WebGL needs float
 *   triples; SVG is painted by CSS, so reading them here would be pure risk and would reintroduce
 *   colour into a `.tsx` file. The only value this component puts into the DOM for colour is the
 *   integer `--fa-h` — a hue STEP. Every `hsl()` lives in flowArt.css.
 *
 *   No `data:image/svg+xml`. That scheme is deliberately blocked at the avatar chokepoint
 *   (`packages/web/src/avatar.ts`) as an XSS control, so the art is inline SVG in the DOM and stays
 *   inside the same policy.
 *
 *   No network, no worker, no canvas. The scene is a few hundred numbers computed synchronously from
 *   data the page already holds. PC-NFR05 forbids external egress and nothing here would benefit from
 *   it anyway.
 *
 *   No `aspect-ratio`. `docs/design-build/CARD_INVENTORY.md:50` records that nothing in the repo uses
 *   it and every card is a fixed pixel height. Instead the `viewBox` is in REAL CSS PX — its units ARE
 *   px — so `width`/`height` describe the box the caller has, a 2px stroke is 2px at every size, and
 *   `preserveAspectRatio="xMidYMid meet"` letterboxes rather than distorting if the box turns out
 *   slightly off-ratio. `slice` was rejected (it crops nodes away silently, which is dishonest) and so
 *   was `none` (it distorts strokes and turns circles into ovals).
 */
import { memo, useMemo, type CSSProperties } from 'react';
import {
  buildFlowPreview,
  decodeFlowShape,
  describeFlowShape,
  flowSeed,
  normaliseArtName,
  pickHueStep,
  type FlowPreviewModel,
  type FlowShapeNode,
} from '@trailblazer/shared';
import { monogram } from '../avatar';
import { cn } from '../lib/utils';
import { FLUENT_ICON_BOX } from './fluentPaths';
import { buildEmblemScene, buildStructuralScene, type ArtShape } from './flowArtGeometry';
import './flowArt.css';

/**
 * Every optional prop here spells `| undefined` out loud. The repo compiles with
 * `exactOptionalPropertyTypes: true`, under which `shape?: T` means "may be ABSENT" and NOT "may be
 * `undefined`" — so a card that holds its own `shape?: T` and forwards it as `shape={shape}` fails to
 * typecheck against the bare form. Every one of these is forwarded from an optional prop by at least
 * one call site, so writing the union is the honest declaration rather than making six callers each
 * spread a conditional object (the workaround `design/Button.tsx` uses for `whileTap`).
 */
export interface FlowArtProps {
  /** Encoded string, decoded nodes, or null/absent → the emblem. */
  shape?: string | readonly FlowShapeNode[] | null | undefined;
  /** Seeds the hue. Present on every surface, including the closed suggestion payload. */
  name: string;
  /** The rendered box in CSS px. Drives both the viewBox and the detail budget. */
  width: number;
  height: number;
  known?: { connectorCount?: number | null; categoryLabel?: string | null } | undefined;
  /** Non-null ⇒ `role="img"` + this label. Omitted ⇒ `aria-hidden` ornament. No third path. */
  label?: string | null | undefined;
  className?: string | undefined;
}

/* ------------------------------------------------------------------------------------------------
 * Shape resolution — shared by the component and by `flowArtLabel`
 * ---------------------------------------------------------------------------------------------- */

/**
 * Resolve whatever the caller passed into a model, or null when there is no readable structure.
 *
 * A malformed encoded string returns null rather than throwing: `decodeFlowShape` is null-on-garbage by
 * contract, and an unreadable value has to degrade to the emblem. An EMPTY node list is also null —
 * "we read the definition and it had no steps" and "we could not read the definition" are the same
 * thing as far as what can honestly be drawn.
 */
function resolveModel(shape: FlowArtProps['shape']): FlowPreviewModel | null {
  if (!shape) return null;
  const nodes = typeof shape === 'string' ? decodeFlowShape(shape) : shape;
  if (!nodes || nodes.length === 0) return null;
  return buildFlowPreview(nodes);
}

/**
 * The accessible sentence for a shape — *"Flow shape: 17 steps, 3 nesting levels, 2 conditions, 1
 * loop, 4 connectors."* — or null when there is no structure to describe.
 *
 * Exported as a helper rather than generated inside the component so `label` keeps its literal meaning
 * ("non-null ⇒ `role="img"` with THIS text"). A caller that wants the graphic announced passes
 * `label={flowArtLabel(shape)}`; a caller that wants an ornament passes nothing. Those are the only two
 * options, which is exactly the invariant `eslint-plugin-jsx-a11y` is checking for.
 *
 * It is derived from `stats` alone — never from labels, never from author text — so a screen reader
 * gets the same facts a sighted reader takes from the picture, and no disclosure boundary is crossed.
 */
export function flowArtLabel(shape: FlowArtProps['shape']): string | null {
  const model = resolveModel(shape);
  return model ? describeFlowShape(model.stats) : null;
}

/* ------------------------------------------------------------------------------------------------
 * Rendering
 * ---------------------------------------------------------------------------------------------- */

/**
 * One shape to one element. No `<g>` wrappers, no transforms: the geometry already works in the
 * viewBox's own px, so a transform would only add a second coordinate system to reason about.
 *
 * `opacity` is set as an ATTRIBUTE, and no paint class in flowArt.css sets `opacity` (they use
 * `fill-opacity`/`stroke-opacity`). That is what lets a scene's per-shape depth ramp multiply with a
 * paint's own transparency instead of being overridden by it.
 *
 * A shape's `tone` becomes a second CLASS, never a style. The brand hexes live in design-system.css
 * and `.fa__tone--*` in flowArt.css only points at them, so an app's colour reaches the DOM without a
 * colour literal entering this file — the same reason `--fa-h` is an integer. The suffix is a
 * `FlowAppId`, a closed twenty-member union, so an author-controlled connector string can never become
 * part of a class name.
 */
function Mark({ shape }: { shape: ArtShape }) {
  const paint = cn(`fa__${shape.paint}`, shape.tone ? `fa__tone--${shape.tone}` : undefined);
  const opacity = typeof shape.alpha === 'number' ? shape.alpha : undefined;
  switch (shape.kind) {
    case 'rect':
      return (
        <rect
          className={paint}
          x={shape.x}
          y={shape.y}
          width={shape.w}
          height={shape.h}
          rx={shape.rx}
          opacity={opacity}
        />
      );
    case 'circle':
      return <circle className={paint} cx={shape.cx} cy={shape.cy} r={shape.r} opacity={opacity} />;
    case 'ellipse':
      return (
        <ellipse
          className={paint}
          cx={shape.cx}
          cy={shape.cy}
          rx={shape.rx}
          ry={shape.ry}
          opacity={opacity}
        />
      );
    case 'path':
      return <path className={paint} d={shape.d} opacity={opacity} />;
    case 'icon':
      // The one transform in the file, and it earns it: a Fluent path is authored in a 24-unit box, so
      // placing it by centre at an arbitrary size means translate-then-scale. Order matters — SVG
      // applies these right to left, so the path is scaled about its own origin first and the already
      // scaled box is then moved, which is why the offset is in the OUTER units.
      return (
        <path
          className={paint}
          d={shape.d}
          opacity={opacity}
          transform={`translate(${shape.cx - shape.size / 2} ${shape.cy - shape.size / 2}) scale(${
            shape.size / FLUENT_ICON_BOX
          })`}
        />
      );
    case 'text':
      return (
        <text
          className={cn('fa__t', paint)}
          x={shape.x}
          y={shape.y}
          fontSize={shape.size}
          textAnchor={shape.anchor}
          opacity={opacity}
        >
          {shape.text}
        </text>
      );
    default:
      return null;
  }
}

function FlowArtImpl({ shape, name, width, height, known, label, className }: FlowArtProps) {
  const connectorCount = known?.connectorCount ?? null;
  const categoryLabel = known?.categoryLabel ?? null;

  const { scene, hueStep } = useMemo(() => {
    // The HUE is seeded from the NAME, not from the shape and not from a tool id. `name` is the one
    // field present on all six surfaces and in the wizard draft before a tool id exists, so it is the
    // only seed that can make the wizard preview and the eventually-published tile agree. The cost is
    // that renaming a tool re-hues it, which is arguably the correct behaviour.
    const normalised = normaliseArtName(name);
    const step = pickHueStep(flowSeed(normalised));
    const model = resolveModel(shape);
    if (model) {
      return { scene: buildStructuralScene(model, width, height), hueStep: step };
    }
    // The emblem seed is namespaced away from the hue seed so a tool's mark and its tint are not
    // locked to each other — two tools one letter apart should not get the same arcs.
    return {
      scene: buildEmblemScene(flowSeed(normalised, 'emblem'), monogram(name), width, height, {
        connectorCount,
        categoryLabel,
        hasFlow: false,
      }),
      hueStep: step,
    };
    // `known` is destructured to primitives above: callers pass a fresh object literal every render,
    // so depending on it directly would rebuild the scene on every parent re-render.
  }, [shape, name, width, height, connectorCount, categoryLabel]);

  // Exactly two ARIA branches, enforced here rather than left to six call sites. `label` non-null
  // means the graphic carries information the surrounding text does not; absent means the tool's name
  // is already in the DOM beside it, so announcing the tile again would only add noise.
  const labelled = typeof label === 'string' && label.length > 0;

  return (
    <div className={cn('fa', className)} style={{ '--fa-h': hueStep } as CSSProperties}>
      <svg
        className="fa__svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role={labelled ? 'img' : undefined}
        aria-label={labelled ? label : undefined}
        aria-hidden={labelled ? undefined : true}
        focusable="false"
      >
        {scene.shapes.map((s, i) => (
          <Mark key={i} shape={s} />
        ))}
      </svg>
    </div>
  );
}

/**
 * Memoised because the profile page mounts up to twenty of these at once and a parent re-render
 * (hover, a health poll, a filter change) must not recompute twenty scenes. The props are primitives
 * plus an optional `known` object, so the default shallow comparison is the right one.
 */
export const FlowArt = memo(FlowArtImpl);
export default FlowArt;
